import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

// Lazy load pages for performance
const ZKAuthPage = React.lazy(() => import('./pages/ZKAuthPage'));
const ZKEnrollPage = React.lazy(() => import('./pages/ZKEnrollPage'));
const ZKLoginPage = React.lazy(() => import('./pages/ZKLoginPage'));

const zkAuthenticationModuleConfig: ModuleConfig = {
  id: 'zk-authentication',
  name: 'ZK Biometric Authentication',
  description: 'Zero-knowledge biometric authentication using face recognition',
  version: '0.1.0',
  enabled: true,
  category: 'security',
  order: 100, // Put near end of nav
  icon: '🔐',
  color: 'purple',

  // Routes - Public routes for authentication
  routes: [
    {
      path: '/zk-authentication',
      component: ZKAuthPage,
      permissions: [], // Public
      useLayout: true,
    },
    {
      path: '/zk-authentication/enroll',
      component: ZKEnrollPage,
      permissions: [], // Public
      useLayout: true,
    },
    {
      path: '/zk-authentication/login',
      component: ZKLoginPage,
      permissions: [], // Public
      useLayout: true,
    },
  ],

  // Permissions for module management
  permissions: {
    view: [], // Public - no permissions required for basic auth
    create: [],
    edit: ['ADMIN', 'MASTER_ADMIN'],
    delete: ['ADMIN', 'MASTER_ADMIN'],
  },

  // Navigation items
  navigation: [
    {
      label: 'Face Login',
      path: '/zk-authentication',
      icon: '🔐',
      permissions: [],
    },
    {
      label: 'Enroll Face',
      path: '/zk-authentication/enroll',
      icon: '📝',
      permissions: [],
    },
  ],

  // Quick actions
  quickActions: [
    {
      label: 'Set Up Face Login',
      path: '/zk-authentication/enroll',
      icon: '🔐',
      description: 'Enable passwordless authentication with your face',
    },
  ],

  // Changelog
  changelog: [
    {
      version: '0.1.0',
      releaseDate: '2026-01-23',
      title: 'Initial MVP Release',
      features: [
        '✨ Face-based enrollment with liveness detection',
        '✨ Zero-knowledge key derivation from biometrics',
        '✨ Challenge-response authentication',
        '✨ Integration with existing auth module',
        '🔒 Client-side cryptography - private keys never leave browser',
      ],
      notes: 'Initial MVP release with core face authentication features.',
    },
  ],

  // Roadmap
  roadmap: [
    {
      title: 'Voice Second Factor',
      description: 'Add voice recognition as optional second factor',
      targetVersion: 'Q2 2026',
      priority: 'medium',
      status: 'planned',
      features: [
        'Voice signature capture',
        'Voice liveness detection',
        'Combined face + voice authentication',
      ],
    },
    {
      title: 'Web3 Signing',
      description: 'Enable signing blockchain transactions with biometric-derived keys',
      targetVersion: 'Q2 2026',
      priority: 'high',
      status: 'planned',
      features: [
        'Transaction signing UI',
        'Multiple network support',
        'Wallet address derivation',
      ],
    },
    {
      title: 'Full Zero-Knowledge Proofs',
      description: 'Implement circuit-based ZK proofs using snarkjs',
      targetVersion: 'Q3 2026',
      priority: 'low',
      status: 'planned',
      features: [
        'snarkjs integration',
        'Custom circom circuits',
        'On-chain verification option',
      ],
    },
    {
      title: 'KYC Integration',
      description: 'Extend to full KYC verification with document checking',
      targetVersion: 'Q4 2026',
      priority: 'medium',
      status: 'planned',
      features: [
        'Document scanning',
        'Face-to-document matching',
        'Compliance reporting',
        'Third-party verifier integration',
      ],
    },
  ],
};

export default zkAuthenticationModuleConfig;
